// SkipEnquiryEditPage — /skip/enquiries/:id/edit.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import {
  useSkipEnquiry,
  useUpdateSkipEnquiry,
} from '../hooks/useSkipEnquiries';
import { useSkipSizes } from '../hooks/useSkipJobs';
import { useActivatedLegacySkipTypes } from '../hooks/useActivatedLegacySkipTypes';
import { useSkipJobTypes } from '../hooks/useSkipJobTypes';
import {
  SitePicker,
  type SitePickerValue,
} from '../../../components/ui/SitePicker/SitePicker';
import { extractErrorMessage } from '../../../utils/errorMessage';
import { AttachmentSection } from '../components/AttachmentSection';
import { CustomerSearch } from '../../bookings/components/CustomerSearch';
import styles from './SkipEnquiryEditPage.module.css';

const LEGACY_LABELS: Readonly<Record<string, string>> = {
  skip: 'Skip',
  bin: 'Bin',
  roro: 'Roro',
};

interface FormState {
  customer_uuid: string | null;
  customer_name: string;
  customer_email: string | null;
  customer_phone: string;
  customer_postcode: string;
  skip_type: 'skip' | 'bin' | 'roro' | 'other' | '';
  /** Bug 2 — enc_id of a custom (non-legacy) stock type when one is
   *  selected; null otherwise. The persisted skip_type stays 'other'
   *  for customs (the legacy ENUM has no slot for them) and the size
   *  dropdown filters by parent stock type instead of legacy enum. */
  custom_stock_type_uuid: string | null;
  /** Encrypted enc_id of the chosen skip_stock_sizes row. */
  skip_size_enc_id: string | null;
  job_type: string;
  skip_job_type_enc_id: string | null;
  preferred_delivery_date: string;
  follow_up_date: string;
  estimated_price: string;
  is_priority: boolean;
  priority_notes: string;
  notes: string;
  status: string;
  // Phase 6D — site picker state.
  site_enc_id: string | null;
  customer_address_line1: string | null;
  customer_address_line2: string | null;
  customer_address_city: string | null;
  customer_county: string | null;
}

export function SkipEnquiryEditPage(): JSX.Element {
  const { id: encId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const enquiryQuery = useSkipEnquiry(encId);
  const sizesQuery = useSkipSizes();
  const activatedLegacy = useActivatedLegacySkipTypes();
  const jobTypesQuery = useSkipJobTypes({ forSelection: true });
  const update = useUpdateSkipEnquiry(encId);

  const jobTypes = jobTypesQuery.data?.jobTypes ?? [];

  // Saved enquiries may carry a skip_type whose underlying global is
  // now deactivated. Surface it explicitly so the user keeps context;
  // 'other' stays available as a generic catch-all.
  const typeOptions = useMemo(() => {
    const opts: Array<{ value: string; label: string }> = [
      { value: '', label: '— Not specified —' },
      ...activatedLegacy.types.map((t) => ({ value: t, label: LEGACY_LABELS[t] ?? t })),
    ];
    const saved = enquiryQuery.data?.enquiry.skip_type ?? null;
    if (
      saved &&
      saved !== 'other' &&
      !activatedLegacy.types.includes(saved as 'skip' | 'bin' | 'roro')
    ) {
      opts.push({ value: saved, label: `${LEGACY_LABELS[saved] ?? saved} (deactivated)` });
    }
    // Bug 2 — append own-tenant customs as `custom:${enc_id}` options.
    for (const c of activatedLegacy.customTypes) {
      opts.push({ value: `custom:${c.uuid}`, label: c.name });
    }
    opts.push({ value: 'other', label: 'Other' });
    return opts;
  }, [activatedLegacy.types, activatedLegacy.customTypes, enquiryQuery.data]);

  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);

  useEffect(() => {
    const enq = enquiryQuery.data?.enquiry;
    if (!enq || form !== null) return;
    setForm(() => ({
      customer_uuid: enq.customer_uuid ?? null,
      customer_name: enq.customer_name,
      customer_email: enq.customer_email,
      customer_phone: enq.customer_phone,
      customer_postcode: enq.customer_postcode,
      skip_type: (enq.skip_type ?? '') as FormState['skip_type'],
      custom_stock_type_uuid:
        enq.skip_type === 'other' && enq.skip_stock_type_uuid != null
          ? enq.skip_stock_type_uuid
          : null,
      skip_size_enc_id: enq.skip_size_enc_id,
      job_type: enq.job_type ?? '',
      skip_job_type_enc_id: enq.skip_job_type_enc_id ?? null,
      preferred_delivery_date: enq.preferred_delivery_date ?? '',
      follow_up_date: enq.follow_up_date ?? '',
      estimated_price:
        enq.estimated_price !== null ? String(enq.estimated_price) : '',
      is_priority: enq.is_priority,
      priority_notes: enq.priority_notes ?? '',
      notes: enq.notes ?? '',
      status: enq.status,
      // Bug 1 — pre-fill site_enc_id when the enquiry has a saved
      // site. The SitePicker receives initialSiteEncId below so it
      // visually pre-selects; the form also tracks the value so the
      // PUT payload preserves the site_id when nothing else changes.
      site_enc_id: enq.site_enc_id ?? null,
      customer_address_line1: enq.customer_address_line1 ?? null,
      customer_address_line2: enq.customer_address_line2 ?? null,
      customer_address_city: enq.customer_city ?? null,
      customer_county: enq.customer_county ?? null,
    }));
  }, [enquiryQuery.data, form]);

  if (enquiryQuery.isLoading || form === null) {
    return (
      <div className={styles.page}>
        <PageHeader title="Loading…" />
      </div>
    );
  }

  if (enquiryQuery.isError || !enquiryQuery.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="Enquiry not found" />
        <Card>
          <p>This enquiry could not be loaded.</p>
        </Card>
      </div>
    );
  }

  const enquiry = enquiryQuery.data.enquiry;
  const isTerminal =
    enquiry.status === 'converted' ||
    enquiry.status === 'cancelled' ||
    enquiry.status === 'expired';

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (form === null) return;
    setError(null);
    const body: Record<string, unknown> = {
      customer_name: form.customer_name,
      customer_email: form.customer_email,
      customer_phone: form.customer_phone,
      customer_postcode: form.customer_postcode,
      is_priority: form.is_priority,
      notes: form.notes || null,
      status: form.status,
    };
    if (form.customer_uuid) body['customer_uuid'] = form.customer_uuid;
    if (form.skip_type) body['skip_type'] = form.skip_type;
    else body['skip_type'] = null;
    if (form.skip_size_enc_id !== null) body['skip_size_enc_id'] = form.skip_size_enc_id;
    if (form.job_type) body['job_type'] = form.job_type;
    else body['job_type'] = null;
    body['skip_job_type_enc_id'] = form.skip_job_type_enc_id;
    // Phase 6D — site picker payload.
    if (form.site_enc_id !== null) {
      body['site_enc_id'] = form.site_enc_id;
    } else if (form.customer_address_line1) {
      body['site_enc_id'] = null;
      body['customer_address_line1'] = form.customer_address_line1;
      body['customer_address_line2'] = form.customer_address_line2;
      body['customer_city'] = form.customer_address_city;
      body['customer_county'] = form.customer_county;
    }
    body['preferred_delivery_date'] = form.preferred_delivery_date || null;
    body['follow_up_date'] = form.follow_up_date || null;
    body['estimated_price'] = form.estimated_price ? Number(form.estimated_price) : null;
    body['priority_notes'] = form.is_priority ? form.priority_notes || null : null;

    update.mutate(body, {
      onSuccess: () => navigate(`/skip/enquiries/${encodeURIComponent(encId)}`),
      onError: (err) => setError(extractErrorMessage(err)),
    });
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={`Edit Enquiry ${enquiry.enquiry_number}`}
        subtitle="Update enquiry details"
        actions={
          <Button
            variant="secondary"
            icon={ArrowLeft}
            onClick={() => navigate(`/skip/enquiries/${encodeURIComponent(encId)}`)}
          >
            Cancel
          </Button>
        }
      />

      {isTerminal ? (
        <Card>
          <p className={styles.banner}>
            This enquiry is <strong>{enquiry.status}</strong> and cannot be edited.
          </p>
        </Card>
      ) : null}

      <form onSubmit={handleSubmit} className={styles.form}>
        <fieldset className={styles.fieldset} disabled={isTerminal}>
          <Card>
            <h2 className={styles.sectionHeading}>Customer</h2>

            <div className={styles.customerDisplay}>
              <div className={styles.customerInfo}>
                <span className={styles.customerName}>{form.customer_name}</span>
                {form.customer_phone && (
                  <span className={styles.customerMeta}>{form.customer_phone}</span>
                )}
                {form.customer_email && (
                  <span className={styles.customerMeta}>{form.customer_email}</span>
                )}
                {form.customer_postcode && (
                  <span className={styles.customerMeta}>{form.customer_postcode}</span>
                )}
              </div>
              <button
                type="button"
                className={styles.swapBtn}
                onClick={() => setShowCustomerSearch(true)}
              >
                Change customer
              </button>
            </div>

            {showCustomerSearch && (
              <div className={styles.customerSearchPanel}>
                <CustomerSearch
                  newCustomerMode={false}
                  onSelect={(c) => {
                    setForm((prev) =>
                      prev === null
                        ? prev
                        : {
                            ...prev,
                            customer_uuid: c.uuid,
                            customer_name: c.name,
                            customer_phone: c.phone ?? '',
                            customer_email: c.email ?? null,
                            customer_postcode: c.postcode ?? '',
                          },
                    );
                    setShowCustomerSearch(false);
                  }}
                  onToggleNewCustomer={() => {}}
                />
                <button
                  type="button"
                  className={styles.cancelSwap}
                  onClick={() => setShowCustomerSearch(false)}
                >
                  Cancel
                </button>
              </div>
            )}
          </Card>

          <Card>
            <h2 className={styles.sectionHeading}>Job Site</h2>
            <SitePicker
              customerEncId={form.customer_uuid ?? enquiry.customer_uuid}
              initialSiteUuid={enquiry.site_uuid}
              initialJobAddress={{
                line1: enquiry.customer_address_line1 ?? null,
                line2: enquiry.customer_address_line2 ?? null,
                city: enquiry.customer_city ?? null,
                postcode: enquiry.customer_postcode ?? null,
              }}
              onChange={(value: SitePickerValue) => {
                if (value.type === 'site') {
                  setForm((prev) =>
                    prev === null
                      ? prev
                      : {
                          ...prev,
                          site_enc_id: value.site_enc_id,
                          customer_address_line1: null,
                          customer_address_line2: null,
                          customer_address_city: null,
                        },
                  );
                } else if (value.type === 'one_off') {
                  setForm((prev) =>
                    prev === null
                      ? prev
                      : {
                          ...prev,
                          site_enc_id: null,
                          customer_address_line1: value.line1,
                          customer_address_line2: value.line2,
                          customer_address_city: value.city,
                        },
                  );
                }
              }}
            />
          </Card>

          <Card>
            <h2 className={styles.sectionHeading}>Stock / Job Details</h2>
            <div className={styles.grid}>
              <label className={styles.field}>
                <span>Status</span>
                <select
                  value={form.status}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev === null ? prev : { ...prev, status: e.target.value },
                    )
                  }
                >
                  <option value="open">Open</option>
                  <option value="contacted">Contacted</option>
                  <option value="no_answer">No Answer</option>
                  <option value="quoted">Quoted</option>
                </select>
              </label>
              <label className={styles.field}>
                <span>Stock Type</span>
                <select
                  value={
                    form.custom_stock_type_uuid !== null
                      ? `custom:${form.custom_stock_type_uuid}`
                      : form.skip_type
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v.startsWith('custom:')) {
                      const uuid = v.slice('custom:'.length);
                      setForm((prev) =>
                        prev === null
                          ? prev
                          : {
                              ...prev,
                              skip_type: 'other',
                              custom_stock_type_uuid: uuid,
                              skip_size_enc_id: null,
                            },
                      );
                    } else {
                      setForm((prev) =>
                        prev === null
                          ? prev
                          : {
                              ...prev,
                              skip_type: v as FormState['skip_type'],
                              custom_stock_type_uuid: null,
                              skip_size_enc_id: null,
                            },
                      );
                    }
                  }}
                >
                  {typeOptions.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.field}>
                <span>Stock Size</span>
                <select
                  value={form.skip_size_enc_id ?? ''}
                  onChange={(e) => {
                    const nextSize = e.target.value === '' ? null : e.target.value;
                    setForm((prev) =>
                      prev === null ? prev : { ...prev, skip_size_enc_id: nextSize },
                    );
                  }}
                  disabled={
                    form.custom_stock_type_uuid === null &&
                    (form.skip_type === '' || form.skip_type === 'other')
                  }
                >
                  <option value="">
                    {form.custom_stock_type_uuid === null &&
                    (form.skip_type === '' || form.skip_type === 'other')
                      ? '— Select type first —'
                      : '— Select size —'}
                  </option>
                  {(sizesQuery.data?.skipSizes ?? [])
                    .filter((s) =>
                      form.custom_stock_type_uuid !== null
                        ? s.stock_type_uuid === form.custom_stock_type_uuid
                        : form.skip_type !== '' &&
                          form.skip_type !== 'other' &&
                          s.skip_type === form.skip_type,
                    )
                    .map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                </select>
              </label>
              <label className={styles.field}>
                <span>Job Type</span>
                <select
                  value={form.skip_job_type_enc_id ?? ''}
                  onChange={(e) => {
                    const encId = e.target.value || null;
                    const found = jobTypes.find((t) => t.enc_id === encId);
                    setForm((prev) =>
                      prev === null
                        ? prev
                        : {
                            ...prev,
                            skip_job_type_enc_id: encId,
                            job_type: found?.key ?? '',
                          },
                    );
                  }}
                >
                  <option value="">— Not specified —</option>
                  {jobTypes.map((t) => (
                    <option key={t.enc_id} value={t.enc_id}>
                      {t.name}
                      {t.process_immediately ? ' (Immediate)' : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.field}>
                <span>Estimated Price (£)</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.estimated_price}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev === null ? prev : { ...prev, estimated_price: e.target.value },
                    )
                  }
                />
              </label>
              <label className={styles.field}>
                <span>Preferred Delivery</span>
                <input
                  type="date"
                  value={form.preferred_delivery_date}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev === null
                        ? prev
                        : { ...prev, preferred_delivery_date: e.target.value },
                    )
                  }
                />
              </label>
              <label className={styles.field}>
                <span>Follow-up Date</span>
                <input
                  type="date"
                  value={form.follow_up_date}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev === null ? prev : { ...prev, follow_up_date: e.target.value },
                    )
                  }
                />
              </label>
            </div>
          </Card>

          <Card>
            <h2 className={styles.sectionHeading}>Notes &amp; Priority</h2>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={form.is_priority}
                onChange={(e) =>
                  setForm((prev) =>
                    prev === null ? prev : { ...prev, is_priority: e.target.checked },
                  )
                }
              />
              <span>Mark as priority enquiry</span>
            </label>
            {form.is_priority ? (
              <label className={styles.field}>
                <span>Priority notes</span>
                <textarea
                  rows={2}
                  maxLength={2000}
                  value={form.priority_notes}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev === null ? prev : { ...prev, priority_notes: e.target.value },
                    )
                  }
                />
              </label>
            ) : null}
            <label className={styles.field}>
              <span>Notes</span>
              <textarea
                rows={4}
                maxLength={5000}
                value={form.notes}
                onChange={(e) =>
                  setForm((prev) =>
                    prev === null ? prev : { ...prev, notes: e.target.value },
                  )
                }
              />
            </label>
          </Card>

        </fieldset>

        <Card>
          <h2 className={styles.sectionHeading}>Attachments</h2>
          <AttachmentSection
            kind="enquiry"
            encId={encId}
            readOnly={isTerminal}
          />
        </Card>

        {error ? <p className={styles.error}>{error}</p> : null}

        <div className={styles.formActions}>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(`/skip/enquiries/${encodeURIComponent(encId)}`)}
            disabled={update.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={update.isPending || isTerminal}>
            {update.isPending ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  );
}
